/**
 * billToGate.ts — R4 (SPEC invoice-identity-cascade) — AC6 + AC10a.
 *
 * The unresolved (`needs a client`) gate, as a pure function the BILL TO surface
 * + the actions Christa actually uses (PDF download, mark-as-sent) all read. The
 * REACT disable is the VISIBLE layer over R1's SERVER-side check at the PDF path
 * + the status-transition-to-`sent` PUT — this function is NOT the security
 * boundary, it's the affordance that stops her hitting a server 422 she can't
 * interpret.
 *
 * `editsRequired` (AC10a) is the deterministic readiness counter the happy-path
 * test asserts on: 0 ⇒ Ready, no menus needed. A genuinely-blocking unresolved
 * field (the invoice has no client) increments it; a confidently-resolved or
 * confirm-only (Tier-2) field does NOT — a Tier-2 value is "fine, just wants a
 * yes", not a blocker (the red-team's central correction).
 */
import type { ResolvedFieldDto } from './billToSourceTag';

export interface IdentityResolutionDto {
  establishment?: ResolvedFieldDto | null;
  recipients?: ResolvedFieldDto | null;
  billing_address?: ResolvedFieldDto | null;
  default_differs_from_last_sent?: boolean;
  any_unresolved?: boolean;
  any_suggested?: boolean;
  // R4 picker-copy convenience fields (computed by the read-path projection).
  project_draft_count?: number;
  client_invoice_count?: number;
  multi_client_project?: boolean;
  last_sent_number?: string | null;
  /**
   * The PROJECT recipient pin, reported as itself rather than inferred.
   * `count` is how many contacts project_scope_recipient_defaults holds for
   * this scope; `cleared` is V211's deliberate "nobody on this project".
   * null when the invoice has no client scope. Distinct from `recipients`
   * (which carries a SOURCE, not a list) and from the invoice's own contacts.
   */
  project_recipient_pin?: {
    count: number;
    cleared: boolean;
    /** The pinned contacts' names, so the panel can NAME them rather than
     *  assert a set it does not display. */
    names?: string[];
  } | null;
}

export interface GateState {
  /** True when send must be blocked — the genuinely-unresolved needs-a-client. */
  blocked: boolean;
  /** Deterministic readiness counter (AC10a). 0 ⇒ Ready. */
  editsRequired: number;
  /** Plain-language reason for the block, or '' when not blocked. */
  reason: string;
}

/**
 * Compute the gate. `hasClient` is the decisive input: an invoice with NO client
 * (organization_id null) is the blocking `needs a client` case. An invoice that
 * HAS a client but an unresolved establishment is a fillable blank — the DBA may
 * legitimately be "none / use client name" — so it does NOT block send.
 *
 * AC6: the block attaches to PDF download + mark-as-sent. This function gives
 * those entry points `blocked`; the server (R1) enforces it for real.
 */
export function computeGate(args: {
  hasClient: boolean;
  resolution?: IdentityResolutionDto | null;
}): GateState {
  const { hasClient } = args;

  if (!hasClient) {
    return {
      blocked: true,
      editsRequired: 1,
      reason: 'This invoice needs a client before it can be sent.',
    };
  }

  // Client present → not blocked. editsRequired counts only genuinely-blocking
  // states, which there are none of once a client exists. (Tier-2 confirm
  // states are intentionally NOT counted — "fine, just wants a yes".)
  return { blocked: false, editsRequired: 0, reason: '' };
}
